/** The application frame: header, project sidebar, and the outlet for a page. */

import { useState } from "react";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";

import { endpoints } from "@/api/endpoints";
import { useAsync } from "@/hooks/useAsync";
import { useSession } from "@/hooks/useSession";
import { ChangePassword } from "@/pages/ChangePassword";
import { Badge } from "./ui";

function Sidebar() {
  const { projectRef } = useParams();
  const { data } = useAsync(() => endpoints.projects({ limit: 100 }), []);
  const projects = data?.items ?? [];

  return (
    <nav className="sidebar" aria-label="Projects">
      <div className="sidebar__section">
        <div className="sidebar__heading">Projects</div>
        {projects.length === 0 ? (
          <div className="sidebar__link muted">No projects yet</div>
        ) : (
          projects.map((project) => (
            <NavLink
              key={project.id}
              to={`/projects/${project.slug}`}
              className={() =>
                `sidebar__link${project.slug === projectRef ? " sidebar__link--active" : ""}`
              }
            >
              <span className="truncate">{project.name}</span>
              <span className="sidebar__count">{project.script_count}</span>
            </NavLink>
          ))
        )}
      </div>
    </nav>
  );
}

function NotificationBell() {
  // Polled rather than pushed: a count is cheap and a minute of staleness is fine.
  const { data } = useAsync(() => endpoints.unreadCount(), [], { pollMs: 60_000 });
  const unread = data?.unread ?? 0;

  return (
    <Link to="/notifications" title="Notifications" className="button button--ghost button--small">
      Activity
      {unread > 0 && (
        <Badge tone="accent">{unread > 99 ? "99+" : unread}</Badge>
      )}
    </Link>
  );
}

export function Shell() {
  const { me, reload } = useSession();
  const [changingPassword, setChangingPassword] = useState(false);

  const signOut = async () => {
    await endpoints.logout();
    window.location.href = "/";
  };

  if (changingPassword) {
    return (
      <ChangePassword
        forced={false}
        onChanged={() => {
          setChangingPassword(false);
          reload();
        }}
        onCancel={() => setChangingPassword(false)}
      />
    );
  }

  return (
    <div className="app">
      <header className="header">
        <Link to="/" className="header__brand">
          <span className="header__mark" aria-hidden="true">
            D
          </span>
          Delivery Hub
        </Link>

        <div className="header__spacer" />

        <NotificationBell />

        {me?.is_platform_admin && (
          <>
            <Link to="/admin/users" className="button button--ghost button--small">
              People
            </Link>
            <Link to="/admin/audit" className="button button--ghost button--small">
              Audit
            </Link>
          </>
        )}

        <div className="header__user">
          <span className="truncate" style={{ maxWidth: 200 }}>
            {me?.display_name}
          </span>
          {me?.is_platform_admin && <Badge tone="accent">Admin</Badge>}
          <Link to="/account/apps" className="button button--ghost button--small">
            Apps
          </Link>
          <button
            type="button"
            className="button button--small button--ghost"
            onClick={() => setChangingPassword(true)}
          >
            Password
          </button>
          <button type="button" className="button button--small button--ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <div className="app-body">
        <Sidebar />
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
